import { LOCK_FILTERS_RELEASE_DATE } from "@hey-lady/shared/helpers/const";
import { SpeakingLevelCategories } from "@hey-lady/shared/types/graphql";
import { isAfter } from "date-fns";
import { IntlShape } from "react-intl";
import {
  AppliedFilterType,
  DEFAULT_EVENTS_FILTERS,
  DEFAULT_PEOPLE_FILTERS,
  EVENT_DURATION_FILTER,
  EventFilters,
  EventsFilterCategory,
  Filters,
  OCCUPATION_FILTER,
  PeopleEnglishLevelOptions,
  UrlFilterParams,
} from "./consts";

export const getUpdatedEventFilters = (
  filters: EventFilters["events"],
  filter: AppliedFilterType,
) => {
  const filterValue = filters[filter.category as EventsFilterCategory];
  if (Array.isArray(filterValue)) {
    return filterValue.filter((v: any) => {
      if (filter.category === OCCUPATION_FILTER) {
        return v.id !== filter.value;
      }
      if (filter.category === EVENT_DURATION_FILTER) {
        return Number(v) !== Number(filter.value);
      }
      return v !== filter.value;
    });
  }
  return DEFAULT_EVENTS_FILTERS.events[filter.category as EventsFilterCategory];
};

export const translateEventDurationFilter = (duration: number, intl: IntlShape) => {
  return {
    id: duration,
    title: intl.formatMessage({ id: `ENUM_EVENT_DURATION_FILTER_${duration}` }),
  };
};

export const isPostReleaseMember = (userCreatedAt?: string | number | Date | null) => {
  const created =
    userCreatedAt == null
      ? null
      : userCreatedAt instanceof Date
        ? userCreatedAt
        : new Date(userCreatedAt as unknown as string | number);
  return created ? isAfter(created, LOCK_FILTERS_RELEASE_DATE) : false;
};

export const getAppliedEventFiltersNumber = (filters: EventFilters["events"]) => {
  const appliedFilters = Object.entries(filters).filter(([key, value]) => {
    const filterKey = key as keyof EventFilters["events"];
    if (filterKey === "eventTime") {
      return false;
    }
    if (filterKey === "speakingEvents") {
      return false;
    }
    if (filterKey === "groupStudy") {
      return false;
    }
    if (filterKey === "customTimeFrom" || filterKey === "customTimeUntil") {
      return false;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return filters[filterKey] !== DEFAULT_EVENTS_FILTERS.events[filterKey];
  });
  return appliedFilters.length;
};

export const getAppliedPeopleFiltersNumber = (filters: Filters["people"]) => {
  const appliedFilters = Object.entries(filters).filter(([key, value]) => {
    const filterKey = key as keyof Filters["people"];
    if (Array.isArray(value) && key !== "availability") {
      return value.length > 0;
    }
    if (key === "availability") {
      return filters[filterKey][0] !== DEFAULT_PEOPLE_FILTERS.people[filterKey][0];
    }
    return filters[filterKey] !== DEFAULT_PEOPLE_FILTERS.people[filterKey];
  });
  return appliedFilters.length;
};

export const getEventFiltersSearchParams = (
  searchParams: URLSearchParams,
  filters: EventFilters,
) => {
  searchParams.set(UrlFilterParams.Host, filters.events.host);
  searchParams.set(UrlFilterParams.Type, filters.events.type);
  searchParams.set(UrlFilterParams.EnglishLevel, filters.events.englishLevel);
  filters.events.eventDate && searchParams.set(UrlFilterParams.EventDate, filters.events.eventDate);
  searchParams.set(UrlFilterParams.EventTime, filters.events.eventTime?.join(",") || "");
  filters.events.customTimeFrom &&
    searchParams.set(UrlFilterParams.CustomTimeFrom, filters.events.customTimeFrom);
  filters.events.customTimeUntil &&
    searchParams.set(UrlFilterParams.CustomTimeUntil, filters.events.customTimeUntil);
  searchParams.set(UrlFilterParams.Full, String(filters.events.showFullEvents));
  searchParams.set(UrlFilterParams.SpeakingEvents, String(filters.events.speakingEvents));
  searchParams.set(UrlFilterParams.GroupStudy, String(filters.events.groupStudy));
  filters.events.topicId && searchParams.set(UrlFilterParams.TopicId, filters.events.topicId);
  filters.events.topicFilter &&
    searchParams.set(UrlFilterParams.TopicFilter, filters.events.topicFilter);
  searchParams.set(UrlFilterParams.Interests, filters.events.interests?.join(",") || "");
  searchParams.set(UrlFilterParams.EnglishSkills, filters.events.englishSkills?.join(",") || "");
  searchParams.set(UrlFilterParams.Occupations, filters.events.occupations?.join(",") || "");
  searchParams.set(UrlFilterParams.Duration, filters.events.duration?.join(",") || "");
  return searchParams.toString();
};

export const getSpeakingLevelCategoriesFromPeopleEnglishLevels = (
  options: PeopleEnglishLevelOptions[],
) => {
  const speakingLevelCategories: SpeakingLevelCategories[] = [];
  options.forEach((option) => {
    if (option === PeopleEnglishLevelOptions.Intermediate) {
      speakingLevelCategories.push(
        ...[
          SpeakingLevelCategories.IntermediateLow,
          SpeakingLevelCategories.IntermediateMid,
          SpeakingLevelCategories.IntermediateHigh,
        ],
      );
    }
    if (option === PeopleEnglishLevelOptions.Advanced) {
      speakingLevelCategories.push(
        ...[
          SpeakingLevelCategories.AdvancedLow,
          SpeakingLevelCategories.AdvancedMid,
          SpeakingLevelCategories.AdvancedHigh,
        ],
      );
    }
    if (option === PeopleEnglishLevelOptions.Proficient) {
      speakingLevelCategories.push(SpeakingLevelCategories.Proficient);
    }
  });
  return speakingLevelCategories;
};

export const parseTimeString = (timeStr: string | undefined): Date | undefined => {
  if (!timeStr) return undefined;
  try {
    const [time, period] = timeStr.split(" ");
    const [hours, minutes] = time.split(":");
    let hour = parseInt(hours, 10);
    if (period === "PM" && hour !== 12) hour += 12;
    if (period === "AM" && hour === 12) hour = 0;
    const date = new Date();
    date.setHours(hour, parseInt(minutes, 10), 0, 0);
    return date;
  } catch {
    return undefined;
  }
};

export const formatTime = (date: Date | undefined): string | undefined => {
  if (!date) return undefined;
  return new Date(date).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};
